<template>
  <div id="app">
    <Main />
  </div>
</template>

<script>
import Main from './components/main.vue'

export default {
  name: 'App',

  components: {
    Main
  }
}
</script>

<style>

/* =====================================
   COLOR SYSTEM
===================================== */

:root {
  --primary: #6c3bff;
  --primary-dark: #5425d9;
  --secondary: #8b5cf6;
  --accent: #ec4899;

  --bg: #f7f5ff;
  --surface: #ffffff;
  --surface-soft: #fbfaff;

  --text: #24152f;
  --text-secondary: #6f6680;

  --border: #e6ddf2;

  --success: #10b981;
  --warning: #f59e0b;
  --danger: #ef4444;
}


/* =====================================
   RESET
===================================== */

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

html,
body,
#app {
  margin: 0;
  min-height: 100%;
}

body {
  background: var(--bg);
  color: var(--text);

  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  transition:
    background-color 0.3s ease,
    color 0.3s ease;
}

button,
input,
select,
textarea {
  font-family: inherit;
}


/* =====================================
   NORMAL MODE
===================================== */

#app {
  min-height: 100vh;

  background: var(--bg);

  color: var(--text);
}


/* =====================================
   GENERAL TEXT
===================================== */

h1,
h2,
h3,
h4,
h5,
h6 {
  color: var(--text);
}

p {
  color: var(--text-secondary);
}

strong {
  color: var(--text);
}

label {
  color: var(--text);
}


/* =====================================
   CARDS
===================================== */

.card {
  background: var(--surface);
  color: var(--text);

  border-color: var(--border);
}


/* =====================================
   FORMS
===================================== */

.form-control,
.form-select {
  background: var(--surface);

  color: var(--text);

  border-color: var(--border);
}

.form-control::placeholder {
  color: #9a90a8;
}

.form-control:focus,
.form-select:focus {
  background: var(--surface);

  color: var(--text);

  border-color: var(--primary);

  box-shadow:
    0 0 0 0.2rem
    rgba(108, 59, 255, 0.12);
}


/* =====================================
   TABLE
===================================== */

.table {
  color: var(--text);
}

.table > :not(caption) > * > * {
  background: var(--surface);

  color: var(--text);

  border-color: var(--border);
}


/* =====================================
   PAGINATION
===================================== */

.page-link {
  background: var(--surface);

  color: var(--primary);

  border-color: var(--border);
}

.page-link:hover,
.page-item.active .page-link {
  background: var(--primary);

  color: #ffffff;

  border-color: var(--primary);
}


/* =====================================
   DARK THEME
===================================== */

body.dark-theme {
  --bg: #0e0a18;
  --surface: #181225;
  --surface-soft: #211936;

  --text: #f7f3ff;
  --text-secondary: #c0b7cc;

  --border: #392c4d;
}


/* =====================================
   DARK BODY
===================================== */

body.dark-theme {
  background: #0e0a18;

  color: #f7f3ff;
}

body.dark-theme #app {
  background: #0e0a18;

  color: #f7f3ff;
}


/* =====================================
   DARK TEXT
===================================== */

body.dark-theme h1,
body.dark-theme h2,
body.dark-theme h3,
body.dark-theme h4,
body.dark-theme h5,
body.dark-theme h6 {
  color: #f7f3ff !important;
}

body.dark-theme p {
  color: #c0b7cc !important;
}

body.dark-theme strong {
  color: #f7f3ff !important;
}

body.dark-theme label {
  color: #ddd5e6 !important;
}


/* =====================================
   DARK CARDS
===================================== */

body.dark-theme .card,
body.dark-theme .page-container,
body.dark-theme .checkout-card,
body.dark-theme .summary-card,
body.dark-theme .stat-card,
body.dark-theme .seller-stat,
body.dark-theme .quick-card,
body.dark-theme .empty-cart,
body.dark-theme .empty-wishlist {
  background: #181225 !important;

  color: #f7f3ff !important;

  border-color: #392c4d !important;
}


/* =====================================
   DARK FORMS
===================================== */

body.dark-theme .form-control,
body.dark-theme .form-select {
  background: #211936 !important;

  color: #f7f3ff !important;

  border-color: #4a3a5f !important;
}

body.dark-theme .form-control::placeholder {
  color: #978da5 !important;
}

body.dark-theme .form-control:focus,
body.dark-theme .form-select:focus {
  background: #211936 !important;

  color: #ffffff !important;

  border-color: #8b5cf6 !important;
}


/* =====================================
   DARK TABLE
===================================== */

body.dark-theme .table {
  color: #f7f3ff !important;
}

body.dark-theme .table > :not(caption) > * > * {
  background: #181225 !important;

  color: #f7f3ff !important;

  border-color: #392c4d !important;
}

body.dark-theme .table-light {
  --bs-table-bg: #241a39;
  --bs-table-color: #f7f3ff;
}


/* =====================================
   DARK PAGINATION
===================================== */

body.dark-theme .page-link {
  background: #181225 !important;

  color: #c4b5fd !important;

  border-color: #392c4d !important;
}

body.dark-theme .page-link:hover,
body.dark-theme .page-item.active .page-link {
  background: #6c3bff !important;

  color: #ffffff !important;

  border-color: #6c3bff !important;
}


/* =====================================
   DARK BUTTON TEXT
===================================== */

body.dark-theme .btn-light {
  background: #f7f3ff;

  color: #211936;

  border-color: #f7f3ff;
}


/* =====================================
   DARK HR
===================================== */

body.dark-theme hr {
  border-color: #3d304f !important;

  opacity: 1;
}


/* =====================================
   DARK ALERTS
===================================== */

body.dark-theme .alert-info {
  background: #241a39 !important;

  color: #ddd2ff !important;

  border-color: #49386c !important;
}

body.dark-theme .alert-warning {
  background: #3c2d12 !important;

  color: #ffe08a !important;

  border-color: #6a501c !important;
}

body.dark-theme .alert-danger {
  background: #3b1d27 !important;

  color: #ffbdc9 !important;

  border-color: #6b2c40 !important;
}
</style>